import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { getAggregatedStats } from '@/lib/api/aha';
import LoadingState from '@/ui/molecules/LoadingState';
import Eyebrow from '@/ui/atoms/Eyebrow';
import type { AggregatedStatsResponse, Me } from '@/types/api';

// ProgressDashboard — P4 lifetime council stats.
// Spec: openapi.aha.v1.yaml /me/aggregated-stats + ADDENDUM correction 6.
// Three error states: consent_required, declined, unavailable. Each
// degrades gracefully to a subdued counter line.

type ErrCode = 'consent_required' | 'declined' | 'unavailable' | null;

interface ProgressDashboardProps {
  me: Me;
}

export function ProgressDashboard({ me }: ProgressDashboardProps) {
  const { t } = useTranslation();
  const [stats, setStats] = useState<AggregatedStatsResponse | null>(null);
  const [error, setError] = useState<ErrCode>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setStats(null);
    getAggregatedStats(30)
      .then((r) => {
        if (cancelled) return;
        setStats(r);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.errorCode === 'personalization_consent_required') setError('consent_required');
          else if (err.errorCode === 'personalization_consent_declined') setError('declined');
          else if (err.errorCode === 'aggregated_stats_unavailable') setError('unavailable');
          else setError('unavailable');
        } else {
          setError('unavailable');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  if (error === 'unavailable') {
    return (
      <div
        className="text-center py-4 text-ink-3 text-sm"
        data-testid="progress-dashboard-unavailable"
      >
        {t('ahaDash.progress.statsLoading')} — {me.councils_run} {t('ahaDash.progress.lifetimeCount', { count: me.councils_run })}
        <button
          type="button"
          onClick={() => setReloadTick((n) => n + 1)}
          className="ml-3 underline text-ink-2 hover:text-ink"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (error === 'consent_required' || error === 'declined') {
    return (
      <div className="text-center py-4 text-ink-3 text-sm" data-testid="progress-dashboard-consent">
        {me.councils_run}{' '}
        {t('ahaDash.progress.lifetimeCount', { count: me.councils_run })}
        <Link
          to="/account/profile"
          className="ml-3 underline text-vermilion hover:text-vermilion-700"
        >
          {t('ahaDash.progress.enablePersonalization')}
        </Link>
      </div>
    );
  }

  if (!stats) {
    return <LoadingState rows={2} label={t('ahaDash.progress.statsLoading')} />;
  }

  return (
    <div
      className="px-6 py-5 border border-hairline rounded-md bg-paper space-y-4"
      data-testid="progress-dashboard"
    >
      <div className="flex items-baseline gap-3">
        <span className="font-serif text-3xl text-ink">{stats.total_councils}</span>
        <Eyebrow tone="ash">
          {t('ahaDash.progress.lifetimeCount', { count: stats.total_councils })}
        </Eyebrow>
      </div>

      <IntentMixRow byIntent={stats.by_intent} />
      <HeatmapRow daily={stats.daily_council_count} />

      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-hairline">
        <div>
          <div className="font-mono text-[10px] tracking-meta uppercase text-ink-3 mb-1">
            {t('ahaDash.progress.ironKillTotal', { count: stats.total_iron_kills })}
          </div>
          <div className="text-xl font-medium">{stats.total_iron_kills}</div>
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-meta uppercase text-ink-3 mb-1">
            {t('ahaDash.progress.ironKillThisMonth', { count: stats.iron_kills_this_month })}
          </div>
          <div className="text-xl font-medium">{stats.iron_kills_this_month}</div>
        </div>
      </div>
    </div>
  );
}

function IntentMixRow({ byIntent }: { byIntent: Record<string, number> }) {
  const { t } = useTranslation();
  const entries = Object.entries(byIntent).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return (
    <div>
      <div className="font-mono text-[10px] tracking-meta uppercase text-ink-3 mb-2">
        {t('ahaDash.progress.intentMix')}
      </div>
      <div className="flex flex-wrap gap-2">
        {entries.map(([intent, fraction]) => (
          <span
            key={intent}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-hairline rounded"
            data-testid={`intent-mix-${intent}`}
          >
            <span className="text-ink">{intent}</span>
            <span className="text-ink-3">{Math.round(fraction * 100)}%</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function HeatmapRow({ daily }: { daily: Record<string, number> }) {
  const { t } = useTranslation();
  const dates = Object.keys(daily).sort();
  if (dates.length === 0) return null;
  const max = Math.max(1, ...Object.values(daily));
  return (
    <div>
      <div className="font-mono text-[10px] tracking-meta uppercase text-ink-3 mb-2">
        {t('ahaDash.progress.monthlyHeatmap')}
      </div>
      <div className="grid grid-cols-15 gap-[2px]" style={{ gridTemplateColumns: 'repeat(30, minmax(0, 1fr))' }}>
        {dates.slice(-30).map((d) => {
          const count = daily[d] ?? 0;
          const intensity = count === 0 ? 0 : 0.2 + (count / max) * 0.8;
          return (
            <span
              key={d}
              title={`${d}: ${count}`}
              className="block aspect-square rounded-[1px]"
              style={{ background: `rgba(192, 48, 38, ${intensity})` }}
              data-testid={`heatmap-cell-${d}`}
            />
          );
        })}
      </div>
    </div>
  );
}

export default ProgressDashboard;
